import React, { useCallback, useState, useEffect, useRef } from "react";
import Div from "@jumbo/shared/Div";
import DescriptionIcon from "@mui/icons-material/Description";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import HistoryIcon from "@mui/icons-material/History";
import VideoLabelIcon from "@mui/icons-material/VideoLabel";
import DescriptionTab from "./DescriptionTab";
import EditorialTab from "./EditorialTab";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import SubmissionsTab from "./SubmissionsTab";
import compilerServices from "app/services/compiler-service";
import problemServices from "app/services/problem-service";
import { TabPanel } from "components/TabPanel";
import VideosTab from "./VideosTab";
import { StyledTab, StyledTabs } from "components/CustomTabs";
import { HEIGHT_OFFSET } from "app/utils/constants/constants";
import VisualizationTab from "./VisualizationTab";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";

const MobileContentPanel = ({
  problemData,
  loading,
  metadata,
  onSubmissionSelect,
  value,
  setValue,
}) => {
  const [submissions, setSubmissions] = useState(null);
  const [submissionsLoaded, setSubmissionsLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMoreSubmissions, setHasMoreSubmissions] = useState(true);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [languageFilter, setLanguageFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [videoData, setVideoData] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const videoCacheRef = useRef(new Map());

  const { authUser } = useJumboAuth();
  const isLoggedIn = !!authUser;

  // Fetch submissions for a given page
  const fetchSubmissions = useCallback(
    async (pageNumber, allowFetchRegardless = false) => {
      if (
        !problemData ||
        !problemData.id ||
        (!allowFetchRegardless && !hasMoreSubmissions)
      ) {
        setSubmissionsLoaded(true);
        return;
      }
      setSubmissionsLoaded(false);
      try {
        const res = await compilerServices.getUserProblemSubmissions(
          problemData.id,
          pageNumber
        );
        const submissionsArray = Array.isArray(res) ? res : [];
        setSubmissions((prev) => [...(prev || []), ...submissionsArray]);
        setHasMoreSubmissions(submissionsArray.length === 20);
        setSubmissionsLoaded(true);
      } catch (error) {
        console.error("Error fetching submissions:", error);
        setSubmissionsLoaded(true);
      }
    },
    [problemData, hasMoreSubmissions]
  );

  // Fetch video data only when VideosTab is first selected
  const fetchVideos = useCallback(async (problemName) => {
    if (videoCacheRef.current.has(problemName)) {
      setVideoData(videoCacheRef.current.get(problemName));
      return;
    }
    if (!problemName) return;

    setVideoLoading(true);
    try {
      const videos = await problemServices.getVideoUrl(problemName);
      videoCacheRef.current.set(problemName, videos);
      setVideoData(videos);
    } catch (error) {
      console.error(`Error fetching videos for ${problemName}:`, error);
      setVideoData([]);
    } finally {
      setVideoLoading(false);
    }
  }, []);

  // Handle tab change
  const handleTabChange = useCallback(
    (event, newValue) => {
      setValue(newValue);
      if (newValue === 2 && problemData?.name) {
        // Fetch videos only for VideosTab
        fetchVideos(problemData.name);
      }
      // Remove submission fetching logic from here
    },
    [setValue, problemData, fetchVideos]
  );

  // Fetch videos and submissions when problem or tab changes
  useEffect(() => {
    if (problemData?.name && value === 2) {
      fetchVideos(problemData.name);
    }
    if (problemData?.id && value === 4 && submissions === null) {
      setSubmissions([]);
      setPage(0);
      setHasMoreSubmissions(true);
      fetchSubmissions(0);
    }
  }, [
    problemData?.id,
    problemData?.name,
    value,
    fetchVideos,
    fetchSubmissions,
    submissions,
  ]);

  const handleStatusClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
    setLanguageAnchorEl(null);
  };

  const handleLanguageClick = (event) => {
    setLanguageAnchorEl(event.currentTarget);
    setStatusAnchorEl(null);
  };

  const handleClose = () => {
    setStatusAnchorEl(null);
    setLanguageAnchorEl(null);
  };

  const handleStatusSelect = (status) => {
    setStatusFilter(status === "All Statuses" ? null : status);
    handleClose();
  };

  const handleLanguageSelect = (language) => {
    setLanguageFilter(language === "All Languages" ? null : language);
    handleClose();
  };

  const handleRefresh = () => {
    if (!problemData || !problemData.id) {
      setSubmissions([]);
      setPage(0);
      setHasMoreSubmissions(true);
      setSubmissionsLoaded(true);
      setLanguageFilter(null);
      setStatusFilter(null);
      return;
    }
    setSubmissions([]);
    setPage(0);
    setHasMoreSubmissions(true);
    setLanguageFilter(null);
    setStatusFilter(null);
    fetchSubmissions(0, true);
  };

  return (
    <Div
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Div
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
        }}
      >
        <StyledTabs
          value={value}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flexGrow: 1 }}
          allowScrollButtonsMobile
          aria-label="Tabs"
        >
          <StyledTab
            icon={<DescriptionIcon />}
            iconPosition="start"
            label="Description"
          />
          <StyledTab
            icon={<ImportContactsIcon />}
            iconPosition="start"
            label="Editorial"
          />
          <StyledTab
            icon={<VideoLabelIcon />}
            iconPosition="start"
            label="Videos"
          />
          <StyledTab
            icon={<ShowChartIcon />}
            iconPosition="start"
            label="Visualizations"
          />
          <StyledTab
            icon={<HistoryIcon />}
            iconPosition="start"
            label="Submissions"
          />
        </StyledTabs>
      </Div>
      <TabPanel value={value} index={0} sx={{ p: 0 }}>
        <DescriptionTab problemData={problemData} />
      </TabPanel>
      <TabPanel value={value} index={1} sx={{ p: 0 }}>
        <EditorialTab problemData={problemData} loading={loading} />
      </TabPanel>
      <TabPanel
        value={value}
        index={2}
        sx={{
          p: 0,
          minWidth: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <VideosTab
          problemData={problemData}
          isLoading={videoLoading}
          videos={videoData}
        />
      </TabPanel>
      <TabPanel value={value} index={3} sx={{ p: 0 }}>
        <VisualizationTab problemData={problemData} />
      </TabPanel>
      <TabPanel
        value={value}
        index={4}
        sx={{ p: 0, height: `calc(100vh - ${HEIGHT_OFFSET.DEFAULT})` }}
      >
        <SubmissionsTab
          submissions={submissions}
          submissionsLoaded={submissionsLoaded}
          statusAnchorEl={statusAnchorEl}
          languageAnchorEl={languageAnchorEl}
          languageFilter={languageFilter}
          statusFilter={statusFilter}
          handleStatusClick={handleStatusClick}
          handleLanguageClick={handleLanguageClick}
          handleClose={handleClose}
          handleStatusSelect={handleStatusSelect}
          handleLanguageSelect={handleLanguageSelect}
          metadata={metadata}
          onSubmissionSelect={onSubmissionSelect}
          fetchMoreSubmissions={() => {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchSubmissions(nextPage);
          }}
          hasMoreSubmissions={hasMoreSubmissions}
          handleRefresh={handleRefresh}
          isLoggedIn={isLoggedIn}
        />
      </TabPanel>
    </Div>
  );
};

export default MobileContentPanel;
